import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import HelpIcon from '@mui/icons-material/Help';
import SchoolIcon from '@mui/icons-material/School';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import { Link as RouterLink } from 'react-router-dom';

const frequentArticles = [
  'Como buscar processos no DataJud',
  'Como usar o Chat com IA para análise jurídica',
  'Como salvar processos nos favoritos',
  'Como comparar múltiplos processos',
  'Como exportar dados de processos',
  'Como usar as estatísticas do CNJ',
  'Como navegar no histórico de buscas',
  'Como criar e editar documentos',
  'Como gerenciar créditos e assinatura',
  'Como usar filtros avançados de busca'
];

const faqItems = [
  {
    category: 'Busca de Processos (DataJud)',
    items: [
      {
        question: 'Como buscar processos no DataJud?',
        answer: 'Acesse a página "Pesquisar" no menu lateral. Você pode buscar por número CNJ, assunto/classe processual, busca avançada ou por partes do processo. Selecione o tribunal desejado (TJSP, TJRJ, etc.) e clique em "Buscar". A plataforma consulta diretamente a API pública do CNJ.'
      },
      {
        question: 'Como salvar processos nos favoritos?',
        answer: 'Após realizar uma busca, clique em "Ver Detalhes" no processo desejado e depois em "Adicionar aos Favoritos". Seus processos favoritos aparecerão em um painel no topo da página de busca para acesso rápido.'
      },
      {
        question: 'Como comparar múltiplos processos?',
        answer: 'Na página de busca, clique no botão "Comparar" em até 3 processos. Um painel flutuante aparecerá no canto inferior direito. Clique em "Ver Comparação" para visualizar os processos lado a lado com todos os detalhes organizados.'
      },
      {
        question: 'Como exportar dados de processos?',
        answer: 'Nos detalhes expandidos de cada processo, você encontrará botões para "Exportar Dados" (formato estruturado) e "Copiar JSON" (dados técnicos). O export gera um arquivo de texto com todas as informações do processo formatadas.'
      },
      {
        question: 'Como usar o histórico de buscas?',
        answer: 'Suas últimas 10 buscas são salvas automaticamente. Na página de pesquisa, clique em "Ver Histórico" para visualizar e repetir buscas anteriores. O histórico inclui os parâmetros usados e o número de resultados encontrados.'
      }
    ]
  },
  {
    category: 'Chat IA e Inteligência Artificial',
    items: [
      {
        question: 'Como usar o Chat IA para análise jurídica?',
        answer: 'Acesse a página "Chat IA" no menu lateral. Você pode fazer perguntas sobre direito, solicitar análises de casos, pedir sugestões de argumentos jurídicos, ou obter ajuda para redigir documentos. A IA é treinada especificamente para questões jurídicas brasileiras.'
      },
      {
        question: 'Posso enviar documentos para o Chat IA analisar?',
        answer: 'Sim! No chat, você pode fazer upload de documentos (PDF, DOCX, TXT) para que a IA analise o conteúdo, identifique pontos importantes, sugira melhorias ou responda perguntas específicas sobre o documento.'
      },
      {
        question: 'O Chat IA tem limite de uso?',
        answer: 'O uso do Chat IA consome créditos da sua conta. Cada mensagem ou análise utiliza uma quantidade específica de créditos. Você pode acompanhar seu consumo na página "Meu Consumo" e gerenciar seus créditos no perfil.'
      },
      {
        question: 'Como o Chat IA pode ajudar com peças processuais?',
        answer: 'O Chat IA pode ajudar na elaboração de petições, contestações, recursos e outros documentos jurídicos. Você pode solicitar estruturas, argumentos, jurisprudências relevantes, ou revisão de textos já elaborados.'
      },
      {
        question: 'As conversas do Chat IA ficam salvas?',
        answer: 'Sim, todas as suas conversas ficam salvas no histórico do chat. Você pode acessar conversas anteriores, continuar discussões e organizar seus diálogos por temas ou casos específicos.'
      }
    ]
  },
  {
    category: 'Documentos e Editor',
    items: [
      {
        question: 'Como criar um novo documento?',
        answer: 'Acesse a página "Documentos" no menu lateral e clique em "+ Novo Documento". Você pode escolher entre diferentes tipos de documentos jurídicos ou começar com um documento em branco. O editor possui recursos avançados de formatação jurídica.'
      },
      {
        question: 'Como usar o editor avançado de documentos?',
        answer: 'O editor oferece formatação específica para documentos jurídicos, numeração automática, modelos de petições, e integração com IA para sugestões de texto. Use a barra de ferramentas para formatação e o painel lateral para recursos de IA.'
      },
      {
        question: 'Posso importar documentos existentes?',
        answer: 'Sim! Você pode fazer upload de documentos em formato PDF, DOCX ou TXT diretamente no editor. O sistema converte automaticamente para o formato editável da plataforma.'
      },
      {
        question: 'Como organizar meus documentos?',
        answer: 'Na página "Documentos", você pode organizar por pastas, usar tags, filtrar por data ou tipo, e utilizar a busca para encontrar documentos específicos. Cada documento pode ter categorias e etiquetas personalizadas.'
      },
      {
        question: 'Os documentos ficam salvos automaticamente?',
        answer: 'Sim! O sistema salva automaticamente suas alterações a cada poucos segundos. Você também pode usar Ctrl+S para salvar manualmente. Um indicador mostra o status do salvamento no editor.'
      }
    ]
  },
  {
    category: 'Estatísticas, Créditos e Conta',
    items: [
      {
        question: 'Como acessar as estatísticas do CNJ?',
        answer: 'Acesse a página "Estatísticas" no menu lateral para visualizar o painel oficial de estatísticas do Poder Judiciário do CNJ. Os dados incluem informações sobre processos pendentes, produtividade e tempos processuais de todos os tribunais brasileiros.'
      },
      {
        question: 'Como gerenciar meus créditos?',
        answer: 'Acesse "Meu Consumo" no menu para visualizar seu saldo de créditos, histórico de uso e estatísticas detalhadas. Cada funcionalidade consome uma quantidade específica de créditos (Chat IA, análises, etc.). Você pode adquirir mais créditos em "Meu Perfil".'
      },
      {
        question: 'Como alterar informações do meu perfil?',
        answer: 'Clique no seu nome no canto superior direito e selecione "Perfil". Lá você pode alterar dados pessoais, informações profissionais, configurações de notificação e preferências da conta. Lembre-se de salvar as alterações.'
      },
      {
        question: 'Como visualizar meu histórico de atividades?',
        answer: 'Na página "Dashboard" você encontra um resumo das suas atividades recentes: buscas realizadas, documentos criados, créditos utilizados e estatísticas de uso. Para detalhes completos, acesse "Meu Consumo".'
      },
      {
        question: 'A plataforma é segura para dados sensíveis?',
        answer: 'Sim! Utilizamos criptografia SSL/TLS para todas as conexões, autenticação segura, backup diário dos dados e seguimos as melhores práticas de segurança. Todos os dados são armazenados em conformidade com a LGPD e nunca são compartilhados sem autorização.'
      }
    ]
  }
];

const tutorials = [
  {
    title: 'Primeiros passos na plataforma',
    duration: '5:20',
    level: 'Iniciante',
    views: 3240
  },
  {
    title: 'Como usar a IA para elaborar petições',
    duration: '8:45',
    level: 'Intermediário',
    views: 2815
  },
  {
    title: 'Gestão eficiente do fluxo de trabalho',
    duration: '7:15',
    level: 'Avançado',
    views: 1982
  },
  {
    title: 'Integrando com sistemas de peticionamento',
    duration: '10:30',
    level: 'Avançado',
    views: 1574
  }
];

const categories = [
  {
    title: 'Busca de Processos (DataJud)',
    description: 'Como usar a integração com a API do CNJ para buscar, salvar e comparar processos judiciais.',
    icon: <SchoolIcon fontSize="large" color="primary" />,
    tags: ['DataJud', 'CNJ', 'Busca', 'Favoritos']
  },
  {
    title: 'Chat IA e Análise Jurídica',
    description: 'Aprenda a usar a inteligência artificial para análise de documentos e consultoria jurídica.',
    icon: <ChatIcon fontSize="large" color="primary" />,
    tags: ['IA', 'Chat', 'Análise', 'Consulta']
  },
  {
    title: 'Documentos e Editor',
    description: 'Como criar, editar e organizar documentos jurídicos com o editor avançado.',
    icon: <DescriptionIcon fontSize="large" color="primary" />,
    tags: ['Documentos', 'Editor', 'Modelos', 'Upload']
  },
  {
    title: 'Estatísticas do CNJ',
    description: 'Como acessar e interpretar as estatísticas oficiais do Poder Judiciário.',
    icon: <ArticleIcon fontSize="large" color="primary" />,
    tags: ['Estatísticas', 'CNJ', 'Dados', 'Tribunais']
  },
  {
    title: 'Créditos e Conta',
    description: 'Gestão de créditos, perfil, histórico de uso e configurações da conta.',
    icon: <PaymentIcon fontSize="large" color="primary" />,
    tags: ['Créditos', 'Perfil', 'Consumo', 'Conta']
  },
  {
    title: 'Dúvidas Frequentes',
    description: 'Respostas rápidas para as perguntas mais comuns sobre a plataforma.',
    icon: <HelpIcon fontSize="large" color="primary" />,
    tags: ['FAQ', 'Suporte', 'Ajuda', 'Dúvidas']
  }
];

const HelpPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [feedbackSent, setFeedbackSent] = useState<Record<string, boolean>>({});

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFeedback = (id: string, isPositive: boolean) => {
    // Em produção, enviaria esta informação para o backend
    console.log(`Feedback ${isPositive ? 'positivo' : 'negativo'} para o artigo ${id}`);
    setFeedbackSent(prev => ({ ...prev, [id]: true }));
  };

  // Função para filtrar conteúdo baseado na busca
  const filteredArticles = frequentArticles.filter(article =>
    article.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFAQs = faqItems.map(category => ({
    ...category,
    items: category.items.filter(item =>
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.items.length > 0);

  const filteredCategories = categories.filter(category =>
    category.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const hasSearchResults = searchTerm && (filteredArticles.length > 0 || filteredFAQs.length > 0 || filteredCategories.length > 0);
  const hasNoResults = searchTerm && !hasSearchResults;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Central de Ajuda
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Encontre respostas sobre busca no DataJud, Chat IA, documentos e todas as funcionalidades da plataforma
        </Typography>
        
        {/* Search */}
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Procure na central de ajuda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Resultados da busca */}
      {hasNoResults && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum resultado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tente usar outros termos de busca ou navegue pelas categorias abaixo.
          </Typography>
        </Paper>
      )}

      {/* Quick Access Categories */}
      {(!searchTerm || filteredCategories.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 6 }}>
          {(searchTerm ? filteredCategories : categories).map((category, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card sx={{ height: '100%', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {category.icon}
                </Box>
                <Typography variant="h6" component="h2" gutterBottom align="center">
                  {category.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  {category.description}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  {category.tags.map((tag, i) => (
                    <Chip key={i} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center' }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  sx={{ borderRadius: 4 }}
                  onClick={() => {
                    if (category.title === 'Busca de Processos (DataJud)') {
                      window.open('/search', '_blank');
                    } else if (category.title === 'Chat IA e Análise Jurídica') {
                      window.open('/chat', '_blank');
                    } else if (category.title === 'Documentos e Editor') {
                      window.open('/documents', '_blank');
                    } else if (category.title === 'Estatísticas do CNJ') {
                      window.open('/statistics', '_blank');
                    } else if (category.title === 'Créditos e Conta') {
                      window.open('/profile', '_blank');
                    } else {
                      setTabValue(1); // Vai para a aba de FAQs
                    }
                  }}
                >
                  {category.title === 'Dúvidas Frequentes' ? 'Ver FAQs' : 'Ir para Página'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}

      {/* Tabs Section */}
      <Box sx={{ width: '100%', mb: 6 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="help content tabs" centered>
            <Tab label="Artigos Frequentes" />
            <Tab label="FAQs" />
            <Tab label="Tutoriais em Vídeo" />
          </Tabs>
        </Box>
        
        {/* Artigos Frequentes */}
        <Box hidden={tabValue !== 0} sx={{ py: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {searchTerm ? `Artigos encontrados (${filteredArticles.length})` : 'Artigos lidos frequentemente'}
            </Typography>
            {searchTerm && filteredArticles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Nenhum artigo encontrado para "{searchTerm}"
              </Typography>
            ) : (
              <List>
                {(searchTerm ? filteredArticles : frequentArticles).map((article, index) => (
                <React.Fragment key={index}>
                  <ListItem 
                    button 
                    sx={{ 
                      borderRadius: 1,
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      px: 2
                    }}
                    component={RouterLink}
                    to={`/help/article/${index + 1}`}
                    secondaryAction={
                      <Tooltip title="Salvar para ler depois">
                        <IconButton edge="end" aria-label="bookmark">
                          <BookmarkBorderIcon />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemText 
                      primary={article} 
                      secondary={index % 3 === 0 ? "Atualizado recentemente" : ""}
                    />
                  </ListItem>
                  {index < (searchTerm ? filteredArticles : frequentArticles).length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
              </List>
            )}
            {!searchTerm && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                  color="primary" 
                  variant="outlined" 
                  sx={{ borderRadius: 4 }}
                  component={RouterLink}
                  to="/help/articles"
                >
                  Ver todos os artigos
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
        
        {/* FAQs */}
        <Box hidden={tabValue !== 1} sx={{ py: 3 }}>
          {searchTerm && filteredFAQs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Nenhuma FAQ encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Não encontramos nenhuma pergunta ou resposta para "{searchTerm}"
              </Typography>
            </Paper>
          ) : (
            (searchTerm ? filteredFAQs : faqItems).map((faqCategory, categoryIndex) => (
            <Paper sx={{ p: 3, mb: 3 }} key={categoryIndex}>
              <Typography variant="h6" gutterBottom color="primary">
                {faqCategory.category}
              </Typography>
              
              {faqCategory.items.map((item, itemIndex) => {
                const itemId = `faq-${categoryIndex}-${itemIndex}`;
                return (
                  <Accordion key={itemIndex} sx={{ mt: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${itemId}-content`}
                      id={`panel-${itemId}-header`}
                    >
                      <Typography variant="subtitle1">{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                        {item.answer}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Esta resposta foi útil?
                        </Typography>
                        
                        {feedbackSent[itemId] ? (
                          <Typography variant="caption" color="success.main">
                            Obrigado pelo seu feedback!
                          </Typography>
                        ) : (
                          <Box>
                            <IconButton 
                              size="small" 
                              onClick={() => handleFeedback(itemId, true)}
                              color="primary"
                            >
                              <ThumbUpAltOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleFeedback(itemId, false)}
                            >
                              <ThumbDownAltOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Paper>
          ))
          )}
        </Box>
        
        {/* Tutoriais em Vídeo */}
        <Box hidden={tabValue !== 2} sx={{ py: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tutoriais em Vídeo
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {tutorials.map((tutorial, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined">
                    <Box sx={{ position: 'relative' }}>
                      <Box 
                        sx={{ 
                          height: 180, 
                          backgroundColor: 'rgba(0, 0, 0, 0.06)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <IconButton 
                          size="large" 
                          sx={{ 
                            p: 2,
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'white', opacity: 0.9 }
                          }}
                        >
                          <PlayCircleOutlineIcon fontSize="large" color="primary" />
                        </IconButton>
                      </Box>
                      <Chip 
                        label={tutorial.duration} 
                        size="small" 
                        sx={{ 
                          position: 'absolute', 
                          bottom: 8, 
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white'
                        }} 
                      />
                      <Chip 
                        label={tutorial.level} 
                        size="small" 
                        color={
                          tutorial.level === 'Iniciante' ? 'success' : 
                          tutorial.level === 'Intermediário' ? 'primary' : 'secondary'
                        }
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          left: 8
                        }} 
                      />
                    </Box>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {tutorial.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tutorial.views.toLocaleString()} visualizações
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button color="primary" variant="outlined" sx={{ borderRadius: 4 }}>
                Ver biblioteca completa
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Contact */}
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Não encontrou o que procurava?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Nossa equipe de suporte está pronta para ajudar você a resolver qualquer dúvida.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<ChatIcon />}
            component={RouterLink}
            to="/chat"
            sx={{ borderRadius: 4 }}
          >
            Conversar no chat
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            component="a"
            href="mailto:suporte@advogadaparceira.com"
            sx={{ borderRadius: 4 }}
          >
            Enviar um email
          </Button>
          <Button
            variant="outlined"
            startIcon={<HelpIcon />}
            component={RouterLink}
            to="/status"
            sx={{ borderRadius: 4 }}
          >
            Status do Sistema
          </Button>
        </Box>
      </Paper>

      {/* Self-Service Tools */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom align="center">
          Ferramentas de Autoatendimento
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Button 
              fullWidth 
              variant="outlined" 
              sx={{ py: 2, borderRadius: 2 }}
              component={RouterLink}
              to="/status"
            >
              Status dos Serviços
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button 
              fullWidth 
              variant="outlined" 
              sx={{ py: 2, borderRadius: 2 }}
              component={RouterLink}
              to="/reset-password"
            >
              Recuperar Senha
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button 
              fullWidth 
              variant="outlined" 
              sx={{ py: 2, borderRadius: 2 }}
              component={RouterLink}
              to="/keyboard-shortcuts"
            >
              Atalhos de Teclado
            </Button>
          </Grid>
        </Grid>
      </Paper>

    </Container>
  );
};

export default HelpPage; 